import { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart3, ChevronRight } from 'lucide-react';
import ModulLayout, { PANEL_OD } from '../../components/layout/ModulLayout';
import { stylySpizirny } from '../../lib/spizirnaUi';
import FlekyListPanel, { NAPRIC } from './FlekyListPanel';
import { NapricIkona } from './FlekIkona';
import FlekDetailPanel from './FlekDetailPanel';
import FlekStatistika from './FlekStatistika';
import PokladnaPohledavkyTab from './PokladnaPohledavkyTab';
import PokladnaReportyTab from './PokladnaReportyTab';
import PokladnaNastaveniTab from './PokladnaNastaveniTab';
import PridatPohybModal from './PridatPohybModal';
import ProvestAuditModal from './ProvestAuditModal';
import UzavritFlekModal from './UzavritFlekModal';
import OtevritFlekModal from './OtevritFlekModal';
import VyuctovaniInspector from './VyuctovaniInspector';
import ZpetTlacitko from '../../components/ui/ZpetTlacitko';

const ACCENT = '#3B82F6';

/**
 * Pokladna v třísloupcovém standardu (`standard-rozvrzeni.md`).
 *
 *   seznam fleků  |  detail fleku se záložkami  |  statistika fleku
 *
 * Předchozí podoba byla dashboard + jeden boční inspektor se zásobníkem
 * pohledů: sedm oblastí modulu se střídalo v jednom panelu a z toho, co
 * zrovna vidím, nešlo poznat, ke kterému fleku to patří. Fleky jsou přitom
 * jediná entita, kterou modul spravuje — patří do seznamu.
 *
 * Fleků jsou čtyři, takže přehled přes celou šířku nese dnešní čísla; jinak
 * by to byla poloprázdná obrazovka, kterou norma zakazuje.
 */
export default function PokladnaModul({ user, isDark = false, isMobile = false, rezervaDole = null }) {
    const isCeo = user?.role === 'superadmin';
    const isSpravce = ['admin', 'superadmin'].includes(user?.role);
    // Tentýž balík stylů jako Spižírna, jen s modrou Financí — sklo karet,
    // odstíny textu i pole se tím drží Surovin bez opisování hodnot.
    const { cardStyle, mutedColor, secondaryColor, inputStyle } = useMemo(
        () => stylySpizirny(isDark, ACCENT), [isDark],
    );

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

    const [hledani, setHledani] = useState('');
    const [pohled, setPohled] = useState('vse');
    const [vybranoId, setVybranoId] = useState(null);
    const [vybranoNapric, setVybranoNapric] = useState(null);
    const [zalozka, setZalozka] = useState('pohyby');
    const [statistika, setStatistika] = useState(null);

    // Modály akcí fleku.
    //
    // Při překlopení do třísloupcového standardu se sem nepřenesly: `PokladnaTab`
    // (dřívější podoba) je držel u sebe, a protože akce v `FlekDetailPanel`
    // hlásí jen „klikli na mě", dostaly tady všechny za handler `refresh`.
    // Tlačítka tím zůstala na svém místě a přestala cokoli dělat — klik jen
    // tiše přenačetl data, takže to ani nevypadalo jako chyba. Odhalilo se to
    // 26. 9. 2026 na „Přidat pohyb" u hlavní pokladny.
    const [pridatPohybSmena, setPridatPohybSmena] = useState(null);
    const [auditPokladna, setAuditPokladna] = useState(null);
    const [uzavritSmena, setUzavritSmena] = useState(null);
    const [otevritFlek, setOtevritFlek] = useState(false);
    const [schvalitSmenaId, setSchvalitSmenaId] = useState(null);

    // Vejde se třetí sloupec? Když ne, statistika se stěhuje do detailu jako
    // záložka — `ModulLayout` si tuhle mez hlídá sám pro sebe, modul se ale
    // musí rozhodnout, jestli má obsah panelu kam zopakovat.
    const [vejdeSePanel, setVejdeSePanel] = useState(() => window.innerWidth >= PANEL_OD);
    useEffect(() => {
        const h = () => setVejdeSePanel(window.innerWidth >= PANEL_OD);
        h();
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);
    // Roztažení okna záložku Statistika zruší — bez tohohle by po rozšíření
    // zůstal detail prázdný na záložce, která už neexistuje.
    useEffect(() => {
        if (vejdeSePanel && zalozka === 'statistika') setZalozka('pohyby');
    }, [vejdeSePanel, zalozka]);

    const nacti = useCallback((tise = false) => {
        if (!tise) setLoading(true);
        fetch('/api/finance/pokladna/fleky', { credentials: 'same-origin' })
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(setData)
            .catch(() => {})
            .finally(() => { if (!tise) setLoading(false); });
    }, []);
    useEffect(() => { nacti(); }, [nacti, refreshKey]);

    // Statistika se čte až k vybranému fleku — je to třetí sloupec, ne přehled.
    useEffect(() => {
        if (!vybranoId) { setStatistika(null); return; }
        setStatistika(null);
        fetch(`/api/finance/pokladna/fleky/${vybranoId}/statistika`, { credentials: 'same-origin' })
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(setStatistika)
            .catch(() => setStatistika(null));
    }, [vybranoId, refreshKey]);

    const vsechnyFleky = data?.fleky ?? [];
    const flek = vsechnyFleky.find(f => f.id === vybranoId) ?? null;

    // Pohled zužuje sadu, hledání ji pak ještě filtruje — v tomhle pořadí,
    // ať počty v pilulkách odpovídají pohledům, ne aktuálnímu hledání.
    const fleky = useMemo(() => {
        let v = vsechnyFleky;
        if (pohled === 'otevrene') v = v.filter(f => f.smena?.stav === 'otevreno');
        else if (pohled === 'ceka') v = v.filter(f => f.smena?.stav === 'ceka_na_schvaleni');
        else if (pohled === 'neauditovane') v = v.filter(f => f.aktivni && f.priznak === 'audit_stary');
        else if (pohled === 'dnes') v = v.filter(f => f.pohybu_dnes > 0);
        else if (pohled === 'neaktivni') v = v.filter(f => !f.aktivni);
        else v = v.filter(f => f.aktivni);

        const q = hledani.trim().toLowerCase();
        if (q) v = v.filter(f => `${f.nazev} ${f.drzitel ?? ''}`.toLowerCase().includes(q));
        return v;
    }, [vsechnyFleky, pohled, hledani]);

    const vyberFlek = useCallback((id) => {
        setVybranoNapric(null);
        setVybranoId(id);
        setZalozka('pohyby');
    }, []);
    const vyberNapric = useCallback((id) => {
        setVybranoId(null);
        setVybranoNapric(id);
    }, []);
    const zpet = useCallback(() => { setVybranoId(null); setVybranoNapric(null); }, []);

    const vybrano = !!flek || !!vybranoNapric;

    // Karta statistiky se staví jednou a použije se buď ve třetím sloupci,
    // nebo v detailu — podle toho, jestli se panel vejde.
    const karta = () => flek ? (
        <FlekStatistika statistika={statistika} flek={flek} isDark={isDark}
            cardStyle={cardStyle} mutedColor={mutedColor} secondaryColor={secondaryColor}
            onAudit={() => setZalozka('audity')} />
    ) : null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
            data-ladit="pokladna">
            <ModulLayout
                isMobile={isMobile}
                vybrano={vybrano}
                mobilRezim
                mobilPanel={false}
                mobilRezervaDole={rezervaDole}
                barva={ACCENT}
                isDark={isDark}
                seznam={({ siroky }) => (
                    <FlekyListPanel
                        siroky={siroky} kompaktni={isMobile} isDark={isDark}
                        fleky={fleky} vsechnyFleky={vsechnyFleky} souhrn={data?.souhrn} loading={loading}
                        hledani={hledani} setHledani={setHledani}
                        pohled={pohled} setPohled={setPohled}
                        vybranoId={vybranoId} vybranoNapric={vybranoNapric}
                        onVyber={vyberFlek} onVyberNapric={vyberNapric}
                        onNovyFlek={() => setOtevritFlek(true)}
                        cardStyle={cardStyle} mutedColor={mutedColor}
                        secondaryColor={secondaryColor} inputStyle={inputStyle}
                        rezervaDole={isMobile ? rezervaDole : null}
                    />
                )}
                detail={() => vybranoNapric ? (
                    <NapricDetail id={vybranoNapric} isDark={isDark} isMobile={isMobile}
                        cardStyle={cardStyle} mutedColor={mutedColor} isCeo={isCeo}
                        refreshKey={refreshKey} onZpet={zpet}
                        rezervaDole={isMobile ? rezervaDole : null} />
                ) : (
                    <FlekDetailPanel
                        flek={flek} isDark={isDark} cardStyle={cardStyle} mutedColor={mutedColor}
                        isMobile={isMobile} zalozka={zalozka} setZalozka={setZalozka} onZpet={zpet}
                        rezervaDole={isMobile ? rezervaDole : null}
                        // Modály čekají různé tvary: pohyb a uzavření jedou na
                        // pokladní směně, audit na samotné pokladně.
                        onPridatPohyb={(f) => setPridatPohybSmena({
                            id: f.smena.id,
                            pokladna: { nazev: f.nazev, typ: f.typ },
                        })}
                        auditUzavrenych={isSpravce}
                        // Uzavřený flek se porovnává s hotovostí napočítanou při
                        // uzavření (tak to počítá i server).
                        onAudit={(f) => setAuditPokladna({
                            id: f.id, nazev: f.nazev,
                            uzavreny: f.smena?.stav !== 'otevreno',
                            ocekavany_kc: f.smena?.stav === 'otevreno'
                                ? (f.hotovost_kc ?? null)
                                : (f.smena?.koncovy_stav_skutecny_kc ?? null),
                        })}
                        onUzavrit={(f) => setUzavritSmena({ id: f.smena.id })}
                        onSchvalit={(f) => setSchvalitSmenaId(f.smena.id)}
                        statistika={!vejdeSePanel ? karta() : null}
                    />
                )}
                panel={() => karta()}
                mobilPanelTitulek="Statistika"
                mobilPanelIkona={BarChart3}
            />

            {/* Krátké akce zůstávají modální — stojí nad třemi sloupci, ne v nich. */}
            {pridatPohybSmena && (
                <PridatPohybModal
                    smena={pridatPohybSmena} isDark={isDark}
                    onClose={() => setPridatPohybSmena(null)}
                    onSaved={() => { setPridatPohybSmena(null); refresh(); }}
                />
            )}
            {auditPokladna && (
                <ProvestAuditModal
                    pokladna={auditPokladna} isDark={isDark}
                    onClose={() => setAuditPokladna(null)}
                    onSaved={() => { setAuditPokladna(null); refresh(); }}
                />
            )}
            {uzavritSmena && (
                <UzavritFlekModal
                    smena={uzavritSmena} isDark={isDark}
                    onClose={() => setUzavritSmena(null)}
                    onSaved={() => { setUzavritSmena(null); refresh(); }}
                />
            )}
            {otevritFlek && (
                <OtevritFlekModal
                    isDark={isDark}
                    onClose={() => setOtevritFlek(false)}
                    onSaved={() => { setOtevritFlek(false); refresh(); }}
                />
            )}
            {/* Schválení se odehrává v samotném vyúčtování, ne v potvrzovacím
                okně — manažer si před podpisem prochází čísla. Proto inspector,
                a ne modál; po zavření se čísla fleku přenačtou. */}
            {schvalitSmenaId && (
                <VyuctovaniInspector
                    smenaId={schvalitSmenaId} isDark={isDark}
                    onClose={() => { setSchvalitSmenaId(null); refresh(); }}
                />
            )}
        </div>
    );
}

/**
 * Položky, které nejsou flek. Obsah se bere z dosavadních tabů — jsou
 * hotové a s fleky nemají co do činění, takže není co přepisovat.
 */
function NapricDetail({ id, isDark, isMobile, cardStyle, mutedColor, isCeo, refreshKey, onZpet, rezervaDole = null }) {
    const meta = NAPRIC.find(p => p.id === id);
    const hranice = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const spolecne = { cardStyle, isDark, refreshKey };
    const telo = (
        <div className="flex items-start gap-3 min-w-0 flex-1">
            <NapricIkona id={id} size={isMobile ? 42 : 58} radius={isMobile ? 8 : 9} barva={ACCENT} />
            <div className="min-w-0 flex-1">
                <div className="text-[17px] font-bold truncate" style={{ color: 'var(--text-primary)' }}>{meta?.nazev}</div>
                <div className="text-[12px] mt-1" style={{ color: mutedColor }}>{meta?.popis}</div>
            </div>
        </div>
    );
    return (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'}
            style={isMobile ? null : cardStyle}>
            {isMobile ? (
                <div className="flex flex-col" style={{ padding: '8px 12px 10px', gap: 8, flexShrink: 0, borderBottom: `1px solid ${hranice}` }}>
                    <ZpetTlacitko onClick={onZpet} popis="Zpět na seznam" style={{ alignSelf: 'flex-start' }} />
                    {telo}
                </div>
            ) : (
                <div className="flex items-start justify-between p-4" style={{ borderBottom: `1px solid ${hranice}` }}>{telo}</div>
            )}
            {/* Rezerva na plovoucí lištu — bez ní se k poslednímu obsahu nedá dorolovat. */}
            <div className="flex-1 overflow-y-auto p-3" style={{ paddingBottom: rezervaDole || undefined }}>
                {id === 'pohledavky' && <PokladnaPohledavkyTab {...spolecne} isCeo={isCeo} onResit={() => {}} />}
                {id === 'reporty' && <PokladnaReportyTab {...spolecne} />}
                {id === 'konfigurace' && <PokladnaNastaveniTab {...spolecne} isCeo={isCeo} />}
            </div>
        </div>
    );
}
